/**
 * Cell colour of an OT officer, by the timing they were added with ("HHMM-HHMM", the tail of
 * "OT<no>_HHMM-HHMM"). The usual OT timings each get their own colour and every other timing
 * gets the "other" colour, so an OT officer's shift reads straight off the grid.
 *
 * Belongs to the SESSION: everyone working the same session ID sees the same colours (they ride along with
 * the roster state, like the WhatsApp footer), edited under Settings → OT Colours — no admin needed.
 */
import { useSyncExternalStore } from 'react';
import { apiClient } from '@/api/client';
import type { OtColorSettingsPayload } from '@/types/api';

export interface OtColorRule {
  /** HHMM */
  start: string;
  /** HHMM */
  end: string;
  /** #rrggbb */
  color: string;
}

export interface OtColorSettings {
  rules: OtColorRule[];
  /** Any OT timing no rule matches. */
  otherColor: string;
}

/** Soft pastels, kept clear of Main blue (#a2d2ff), SOS pink (#ffc6d9) and the green
 *  break/unassigned tint (#EAF3DE). */
export const DEFAULT_OT_COLOR_SETTINGS: OtColorSettings = {
  rules: [
    // Yellow spans two shifts: the night OT (0630-1000) and the morning OT (1000-1030)
    // that carries on from it, as well as the whole 0630-1030 stretch.
    { start: '0630', end: '1030', color: '#FEF08A' }, // yellow
    { start: '0630', end: '1000', color: '#FEF08A' }, // yellow — night OT
    { start: '1000', end: '1030', color: '#FEF08A' }, // yellow — morning OT
    { start: '1130', end: '1530', color: '#FFC38A' }, // orange
    { start: '1630', end: '2030', color: '#D6C6F5' }, // purple
  ],
  otherColor: '#E5D5C5', // sand
};

/** Darkest a chosen colour may be (HSL lightness), so the black text on a cell stays easy to read. */
export const MIN_OT_COLOR_LIGHTNESS = 0.7;

/** `hex` with its lightness raised to MIN_OT_COLOR_LIGHTNESS if it is darker (hue and saturation kept). */
export function toReadableColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (l >= MIN_OT_COLOR_LIGHTNESS) return hex.toLowerCase();
  const d = max - min;
  const sat = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + 6) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
  }
  const L = MIN_OT_COLOR_LIGHTNESS;
  const c = (1 - Math.abs(2 * L - 1)) * sat;
  const x = c * (1 - Math.abs((h % 2) - 1));
  const m = L - c / 2;
  const [r1, g1, b1] = h < 1 ? [c, x, 0] : h < 2 ? [x, c, 0] : h < 3 ? [0, c, x] : h < 4 ? [0, x, c] : h < 5 ? [x, 0, c] : [c, 0, x];
  const to = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r1)}${to(g1)}${to(b1)}`;
}

const HHMM = /^\d{4}$/;
const HEX = /^#[0-9a-fA-F]{6}$/;

/** "OT5_1130-1530" → "1130-1530"; null when the id carries no timing. */
export function otTiming(officerId: string): { start: string; end: string } | null {
  const m = /^OT\d+_(\d{4})-(\d{4})$/.exec(officerId);
  return m ? { start: m[1], end: m[2] } : null;
}

/** The colour for an OT officer id under `settings`: its rule's colour, else the "other" colour. */
export function otColorFor(officerId: string, settings: OtColorSettings): string {
  const t = otTiming(officerId);
  const rule = t && settings.rules.find((r) => r.start === t.start && r.end === t.end);
  return rule ? rule.color : settings.otherColor;
}

/** Whatever was stored, cleaned up: malformed rules dropped, a bad "other" colour defaulted. */
export function sanitizeOtColorSettings(raw: unknown): OtColorSettings {
  const d = DEFAULT_OT_COLOR_SETTINGS;
  if (!raw || typeof raw !== 'object') return d;
  const { rules, otherColor } = raw as { rules?: unknown; otherColor?: unknown };
  const clean = Array.isArray(rules)
    ? rules.filter((r): r is OtColorRule =>
        !!r && typeof r === 'object'
        && HHMM.test((r as OtColorRule).start) && HHMM.test((r as OtColorRule).end)
        && HEX.test((r as OtColorRule).color))
        .map((r) => ({ start: r.start, end: r.end, color: toReadableColor(r.color) }))
    : d.rules;
  return {
    rules: clean,
    otherColor: typeof otherColor === 'string' && HEX.test(otherColor) ? toReadableColor(otherColor) : d.otherColor,
  };
}

let current: OtColorSettings = DEFAULT_OT_COLOR_SETTINGS;
let version = 0;
const listeners = new Set<() => void>();

export function getOtColorSettings(): OtColorSettings {
  return current;
}

function publish(next: OtColorSettings) {
  if (JSON.stringify(next) === JSON.stringify(current)) return;
  current = next;
  version++;
  listeners.forEach((l) => l());
}

/** Adopt the colours that came back with a roster-state payload (null: the session has not set any). */
export function adoptOtColorSettings(value: OtColorSettingsPayload | null | undefined): void {
  if (value === undefined) return;
  publish(value ? sanitizeOtColorSettings({ rules: value.rules, otherColor: value.other_color }) : DEFAULT_OT_COLOR_SETTINGS);
}

/** Forget what we know — call when the session changes. */
export function resetOtColorState(): void {
  publish(DEFAULT_OT_COLOR_SETTINGS);
}

/**
 * Save new colours for the whole session. Shown at once here; the server then tells every other tab, and
 * ours goes back to what it was if the save fails. null puts back the built-in colours.
 */
export async function saveOtColorSettings(next: OtColorSettings | null): Promise<void> {
  const before = current;
  const clean = next ? sanitizeOtColorSettings(next) : DEFAULT_OT_COLOR_SETTINGS;
  publish(clean);
  try {
    await apiClient.setOtColors(next ? { rules: clean.rules, other_color: clean.otherColor } : null);
  } catch (e) {
    publish(before);
    throw e;
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Re-renders the caller when the OT colours change; returns a number to put in memo deps of
 *  anything that bakes cell colours in (getOfficerColor reads the live settings). */
export function useOtColorVersion(): number {
  return useSyncExternalStore(subscribe, () => version, () => 0);
}
